import type { ReactNode } from "react";

import { cx, panelSurface } from "@akaike/ui";

/** Signal tones a stat's value can carry. `idle` is "no signal" - the value keeps
 *  the default ink, which is what an unknown or neutral state should look like. */
export type StatTone = "ok" | "warn" | "crit" | "brand" | "idle";

interface StatProps {
  label: ReactNode;
  value: ReactNode;
  unit?: ReactNode;
  tone?: StatTone;
  foot?: ReactNode;
  /** For a value that is not a number - a chip row, a wrapped label. Drops the
   *  display size so the content is not stretched to headline proportions. */
  small?: boolean;
  /** Put the tile on a panel surface, for a summary row that stands on its own
   *  rather than sitting inside one. */
  surface?: boolean;
  className?: string;
}

export function Stat({ label, value, unit, tone, foot, small, surface, className }: StatProps) {
  return (
    <div data-slot="stat" className={cx("kpi", surface && panelSurface, className)}>
      <div className="label">{label}</div>
      <div
        className={cx(
          "value num",
          tone !== "idle" && tone,
          small && "text-[13px]/[1.35] whitespace-normal",
        )}
      >
        {value}
        {unit != null && <small> {unit}</small>}
      </div>
      {foot != null && <div className="foot">{foot}</div>}
    </div>
  );
}

/**
 * A Stat on its own surface, with a muted caption underneath.
 *
 * The page-level summary tile: the shape that sits in a row above the content
 * on Brands, Job detail and Store detail. It exists because every one of those
 * call sites was pairing `className="panel"` with a `<span className="faint">`
 * foot by hand - three private copies of this component, one of which had
 * stopped using Stat at all and drifted to its own font size.
 */
export function Kpi({
  foot,
  ...props
}: Omit<StatProps, "className"> & { foot?: ReactNode }) {
  return (
    <Stat
      {...props}
      surface
      foot={foot == null ? undefined : <span className="faint">{foot}</span>}
    />
  );
}
